import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

/** Body class toggled when the app shell (menu + tabs) is present. */
const APP_DIALOG_SAFE_ZONE_CLASS = 'app-dialog-safe-zone';

/** CSS custom property holding the measured top chrome height. */
const APP_DIALOG_SAFE_TOP_PROPERTY = '--app-dialog-safe-top';

const TOP_CHROME_SELECTORS = [
  '.modern-top-chrome',
  '.customMainHeader',
  '.modern-workspace-chrome',
  'app-workspace-tabs-bar',
  '.workspace-tabs-bar',
] as const;

const MENU_BAR_FALLBACK_PX = 44;

/**
 * Keeps Material dialogs and the shell body below the fixed menu bar and tab strip.
 * Applies globally via body CSS variable + styles in styles.scss / modern layout.
 */
@Injectable({ providedIn: 'root' })
export class AppDialogPositionsService {
  private readonly _matDialog = inject(MatDialog);
  private readonly _router = inject(Router);

  private readonly _boundUpdate = () => this.updateSafeTop();
  private _dialogOpenedSub?: Subscription;
  private _navigationSub?: Subscription;
  private _chromeObserver?: ResizeObserver;
  private _initialized = false;

  constructor() {
    this.initialize();
  }

  /** Recalculate after tabs open, close, or the shell chrome height changes. */
  refresh(): void {
    this.observeTopChrome();
    this.updateSafeTop();
  }

  private initialize(): void {
    if (this._initialized || typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    this._initialized = true;

    this.updateSafeTop();
    window.addEventListener('resize', this._boundUpdate);

    this._dialogOpenedSub = this._matDialog.afterOpened.subscribe(() => {
      this.updateSafeTop();
    });

    this._navigationSub = this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSafeTop();
        this.observeTopChrome();
        setTimeout(() => this.updateSafeTop(), 0);
        setTimeout(() => this.updateSafeTop(), 250);
      });

    if (typeof ResizeObserver !== 'undefined') {
      this._chromeObserver = new ResizeObserver(() => this.updateSafeTop());
    }
    this.observeTopChrome();

    // Workspace tabs can render shortly after the shell mounts.
    for (const delayMs of [0, 250, 800]) {
      setTimeout(() => {
        this.observeTopChrome();
        this.updateSafeTop();
      }, delayMs);
    }
  }

  private observeTopChrome(): void {
    if (!this._chromeObserver || typeof document === 'undefined') {
      return;
    }
    const chrome = document.querySelector<HTMLElement>('.modern-top-chrome');
    if (chrome) {
      this._chromeObserver.observe(chrome);
    }
  }

  private updateSafeTop(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const appHeader = document.querySelector<HTMLElement>('.customMainHeader');
    if (!appHeader || appHeader.getBoundingClientRect().height <= 0) {
      // Empty layout (login/onboarding) — no top chrome to preserve.
      document.body.classList.remove(APP_DIALOG_SAFE_ZONE_CLASS);
      document.body.style.removeProperty(APP_DIALOG_SAFE_TOP_PROPERTY);
      return;
    }

    let safeTop = MENU_BAR_FALLBACK_PX;
    for (const selector of TOP_CHROME_SELECTORS) {
      const el = document.querySelector<HTMLElement>(selector);
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) {
        continue;
      }
      safeTop = Math.max(safeTop, Math.ceil(rect.bottom));
    }

    document.body.classList.add(APP_DIALOG_SAFE_ZONE_CLASS);
    document.body.style.setProperty(APP_DIALOG_SAFE_TOP_PROPERTY, `${Math.max(0, safeTop)}px`);
  }
}
